import React, { useRef, useState } from 'react';
import { Card, Row, Col, Button, message, Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import ContainerBody from '@components/ContainerBody';
import { apiClearConsumptionCache } from '@services/ManageDevices/DeviceServices';
import StatisticsCards from './components/StatisticsCards';
import ConsumptionChart from './components/ConsumptionChart';
import DeviceAllocationChart from './components/DeviceAllocationChart';
import DeviceStatusCharts from './components/DeviceStatusCharts';
import CustomerDeviceCharts from './components/CustomerDeviceCharts';
import WarningStatistics from './components/WarningStatistics';
import ConsumptionComparison from './components/ConsumptionComparison';
import DashboardMap from './components/DashboardMap';
import List from './components/List';
import LineIcon from './components/LineIcon';
import './index.css';

const SectionCard = ({
  title,
  description,
  icon,
  extra,
  flush,
  className = '',
  children,
  ...rest
}) => (
  <Card
    bordered={false}
    className={`dash-card ${flush ? 'dash-card--flush' : ''} ${className}`}
    title={
      title && (
        <div className="dash-card__head">
          {icon && (
            <span className="dash-card__icon">
              <LineIcon name={icon} size={18} />
            </span>
          )}
          <div className="dash-card__titles">
            <div className="dash-card__title">{title}</div>
            {description && <div className="dash-card__desc">{description}</div>}
          </div>
        </div>
      )
    }
    extra={extra}
    {...rest}>
    {children}
  </Card>
);

const DashboardV2 = () => {
  const consumptionChartRef = useRef();
  const [clearingCache, setClearingCache] = useState(false);

  const handleRefreshConsumption = async () => {
    Modal.confirm({
      title: 'Xác nhận',
      icon: <ExclamationCircleOutlined />,
      content:
        'Việc cập nhật dữ liệu tiêu thụ theo ngày sẽ mất thời gian khoảng 1 phút. Bạn có đồng ý không?',
      okText: 'Đồng ý',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          setClearingCache(true);
          await apiClearConsumptionCache();
          message.success('Đã cập nhật thành công');
          if (consumptionChartRef.current) {
            consumptionChartRef.current.refetch();
          }
        } catch (error) {
          console.error('Error clearing cache:', error);
          message.error('Không thể cập nhật vui lòng thử lại sau');
        } finally {
          setClearingCache(false);
        }
      }
    });
  };

  return (
    <ContainerBody>
      <div className="dash-modern">
        <header className="dash-header">
          <div>
            <h1 className="dash-header__title">Bảng điều khiển</h1>
          </div>
        </header>

        <StatisticsCards />

        {/* Layout follows the previous dashboard; only the styling is new */}
        <Row gutter={[20, 20]} className="dash-row">
          <Col xs={24} xl={12}>
            <SectionCard
              icon="bar-chart"
              title="Biểu đồ tiêu thụ ngày"
              description="Sản lượng nước tiêu thụ của từng chi nhánh (m³)"
              extra={
                <Button
                  icon={<LineIcon name="refresh" size={15} />}
                  onClick={handleRefreshConsumption}
                  loading={clearingCache}>
                  Cập nhật
                </Button>
              }>
              <ConsumptionChart ref={consumptionChartRef} />
            </SectionCard>
          </Col>
          <Col xs={24} xl={12}>
            <SectionCard
              icon="pie-chart"
              title="Tình trạng thiết bị & khách hàng"
              description="Nhấn vào biểu đồ kết nối / sóng để lọc danh sách thiết bị">
              <div className="donut-quad">
                <DeviceStatusCharts compact />
                <CustomerDeviceCharts compact />
              </div>
            </SectionCard>
          </Col>
        </Row>

        <Row gutter={[20, 20]} className="dash-row">
          <Col xs={24} xl={12}>
            <SectionCard
              icon="wifi"
              title="Kết nối theo chi nhánh"
              description="Số đồng hồ online / offline tại mỗi chi nhánh">
              <DeviceAllocationChart />
            </SectionCard>
          </Col>
          <Col xs={24} xl={12}>
            <Card bordered={false} className="dash-card triage" id="dash-triage">
              <WarningStatistics />
            </Card>
          </Col>
        </Row>

        <Row gutter={[20, 20]} className="dash-row">
          <Col xs={24} xl={12}>
            <SectionCard
              icon="bar-chart"
              title="Biểu đồ tiêu thụ theo kỳ cước"
              description="So sánh 3 kỳ cước gần nhất theo chi nhánh (m³)">
              <ConsumptionComparison />
            </SectionCard>
          </Col>
          <Col xs={24} xl={12}>
            <SectionCard
              flush
              icon="map"
              title="Bản đồ sự cố mạng lưới"
              description="Vị trí các đồng hồ đang có cảnh báo hoặc mất kết nối">
              <DashboardMap />
            </SectionCard>
          </Col>
        </Row>

        <div className="dash-section-title">
          <h2>Danh sách thiết bị</h2>
          <p>Tra cứu, lọc và xuất dữ liệu thiết bị</p>
        </div>
        <div className="dash-list">
          <List />
        </div>
      </div>
    </ContainerBody>
  );
};

export default DashboardV2;
